import { AdObjectType, Campaign, DailyCampaignData, ScoreTotals, Totals } from 'lib/data/models/CampaignObjects';
import { ProviderNames } from 'lib/data/models/Providers';
import { orZero } from 'lib/utils/NumUtils';
import { DATE_FORMAT, isDateToday, mapDaysBetweenDates, now, tzDate } from "utils/DateUtils";
import { getLogger } from "utils/logging";

const { log, warn } = getLogger('ProviderUtils', { color: 'green', enabled: false });

export const ignoreProviderForDev = (pName) => {
    //if (pName == 'Google') return true;
    //if (pName == 'TikTok') return true;
    return false;
}

/**
 * @description Checks each day that all providers have data for it, and that the day is marked complete.
 * @param {*} providers
 * @param {([] | undefined)} accountDataDays
 * @param {*} from
 * @param {*} to
 * @param {*} [timezone]
 * @return {*}
 */
export const hasAllProviderData = (providers, accountDataDays: [] | undefined, from, to, timezone?, forceUpdate = false) => {
    if (!providers || !providers.length) return true;

    if (!accountDataDays || !accountDataDays?.length) {
        log('- No provider data given.', providers.map(p => p.name));
        return false;
    }

    const days = mapDaysBetweenDates(from, to, false, timezone);
    //log(`Has all provider data?`, providers.map(p => p.name), from, to, days, 'data:', accountDataDays);

    // If any day or provider is incomplete, we mark all as incomplete.
    for (const day of days) {
        // we check the day if it's in the future, since we can receive the requests relative to local and not the stores.
        const _day = tzDate(day, timezone); //.toISOString();
        const _today = now(timezone);
        if (_day.isAfter(_today)) {
            log(`${day} is in the future, according to the account (store today: ${_today.format(DATE_FORMAT)} != ${_day.format(DATE_FORMAT)}) Skipping.`);
            continue;
        }

        let dayData: any = accountDataDays.find((d: any) => d && d.day == day);
        if (!dayData) {
            log(`No day data found for: ${day}.`);
            return false;
        }

        for (const p of providers) {
            // skip this provider if no oauth info, or if force-skip (oauth===false)
            if (!p.oauth?.token && p.oauth !== false) { /* log(`No oauth info for ${p.name}, skipping check...`); */ continue; }
            if (ignoreProviderForDev(p.name)) { log(`ignoring provider ${p.name}`); continue; }

            let pData = (dayData.providers || []).find(_p => _p.name == p.name);
            if (!isProviderDataComplete(day, pData, timezone, forceUpdate, p.name)) {
                log(`⛔ ${p.name} not complete`, day, forceUpdate ? 'force' : '');
                return false;
            }
            //log(`${p.name} complete for day ${day} ✅`);
        }
    }

    return true;
}

/**
 * @description Checks if an individual provider data is complete. If the data is today, and it was
 * updated within the refresh time, we consider it "complete", to bypass comlicated caching for now.
 * @param {*} pData
 * @param {*} [timezone]
 * @return {*}
 */
export const isProviderDataComplete = (day, pData, timezone?, forceUpdate = false, pName?) => {
    if (!pData) {
        log(`No provider ${pName || 'unknown'} data for day: ${day}`);
        return false;
    }
    let updated = new Date(pData.updated || MIN_DATE);
    let updatedMinsAgo = parseFloat(((Date.now() - updated.getTime()) / 1000 / 60).toFixed(1));
    let dataRefreshMins = parseFloat((process.env.DATA_REFRESH_MINS || '20'));
    let isToday = isDateToday(day, timezone);

    // if the day being updated is today, consider it updated if it was within DATA_REFRESH_MINS
    if (isToday) {
        if (forceUpdate) {
            log("Force-updating today data...");
            return false;
        }
        if (updatedMinsAgo < dataRefreshMins) {
            log(`✅ ${pData.name} data was updated recently! (${updatedMinsAgo} mins ago)`);
            return true;
        }
        log(`⛔ ${pData.name} data not updated recently (${updatedMinsAgo} mins ago)`);
        return false;
    }

    // only consider these providers complete if they say they are... the other providers need to
    // manually check the 'campaigns' data below (for retrofitting functionality)
    // if (pData.name == ProviderNames.SHOPIFY || pData.name == ProviderNames.ADAYA) {
    //     return pData.isComplete == true;
    // }

    if (pData.isComplete) {
        if (!pData.data?.campaigns && !pData.data?.totals) {
            log(`⛔ ${pData.name} is not complete for ${day} (needs new data format)`);
            return false;
        }
        return true;
    } else {
        log(`⛔ ${pData.name} is not complete for ${day} (${isToday ? 'today' : 'past'}).`);
        return false;
    }


    // all other providers should update if they don't have the new data format.
    return (pData.isComplete) ? true : false;
}


/**
 * @description Makes DailyCampaignData: { dayStart, dayEnd, campaigns, totals } objects
 * from the server's DailyAccountData: { day, providers, totals } data objects.
 * @param {*} accountDataDays
 * @param {*} from
 * @param {*} to
 * @param {*} timezone
 * @return {*}
 */
export function processDailyAccountDatas(accountDataDays, from, to, timezone) {
    try {
        log(`processDailyAccountDatas`, accountDataDays, from, to);

        // checks to prevent adding stats from any particular providers for what reason:
        const getOmittedProviderStats = (providerName) => {
            if (![ProviderNames.ADAYA, ProviderNames.SHOPIFY].includes(providerName)) {
                //console.log(`NOT addding 'revenue' because provider is: ${providerName}`)
                return ['revenue'];
            }
            return undefined;
        }

        if (!accountDataDays || !accountDataDays.length) return accountDataDays;
        let campaignDataDays = {};
        const days = mapDaysBetweenDates(from, to, false, timezone);

        const convertDay = (day, d) => {
            let cd = new DailyCampaignData(day);

            if (d?.providers?.length) {
                d.providers.forEach(p => {
                    // dont add revenue from any provider besides adaya or shopify
                    let dontAdd = getOmittedProviderStats(p.name);
                    //log(`add provider: ${p.name} for ${d.day}:`, p);
                    if (p.data?.campaigns) {
                        // aggregate the data/totals from the data: ie. the campaign collection.
                        p.data.campaigns.forEach(c => {
                            // default to campaign's provider from data, otherwise use the provider of the data.
                            let campaign: Campaign = cd.getCampaign(c.id, c.name, c.provider || p.name);
                            campaign.totals.addData(c.totals, dontAdd);

                            if (c.adsets) {
                                c.adsets.forEach(a => {
                                    let adset = campaign.getAdset(a.id, a.name);
                                    adset.totals.addData(a.totals, dontAdd);

                                    if (a.ads) {
                                        a.ads.forEach(a2 => {
                                            let ad = adset.getAd(a2.id, a2.name);
                                            ad.totals.addData(a2.totals, dontAdd);
                                        });
                                    }
                                })
                            }
                        })
                    } else if (p.data?.totals) {
                        // note:only do this if there are only totals and no campaign data.
                        cd.addTotals(p.data.totals, dontAdd);
                    }
                });

            }

            // scores individual campaigns, will recalculate totals as well
            scoreCampaignData(cd);

            return cd;
        }

        for (const d of days) {
            let ad = accountDataDays.find(ad => ad.day == d);
            campaignDataDays[d] = convertDay(d, ad);
        }

        log(`processDailyAccountDatas result:`, campaignDataDays, from, to);
        return campaignDataDays;
    } catch(e) {
        log(`Exception in processDailyAccountDatas`, e)
        throw e;
    }
}

/**
 * @description Goes through the collect of DailyCampaignData (indexed by day) and returns a single
 * merged DailyCampaignData object for the entire set.
 * @param {*} campaignDataDays
 * @return {*}
 */
export const combineAllDailyCampaignData = (campaignDataDays: { [string]: DailyCampaignData }) => {
    const allCd = new DailyCampaignData(); // no day given, assume multi-range.
    if (!campaignDataDays || !Object.keys(campaignDataDays)) return allCd;

    const days = Object.keys(campaignDataDays);
    allCd.dayStart = days[0];
    allCd.dayEnd = days[days.length - 1];
    //log(`combineAllDailyCampaignData: ${days.length} days`, campaignDataDays);

    // add all campaigns > adsets > and ads for a provider, and their totals
    const _processDayCampaignData = (cd) => {
        if (!cd) return undefined;

        if (cd.campaigns) {
            cd.campaigns.forEach(c => {
                // default to campaign's provider from data, otherwise use the provider of the data.
                let campaign: Campaign = allCd.getCampaign(c.id, c.name, c.provider);
                campaign.totals.addData(c.totals);

                if (c.adsets) {
                    c.adsets.forEach(a => {
                        let adset = campaign.getAdset(a.id, a.name, a.provider || c.provider);
                        adset.totals.addData(a.totals);

                        if (a.ads) {
                            a.ads.forEach(a2 => {
                                let ad = adset.getAd(a2.id, a2.name, a2.provider || c.provider);
                                ad.totals.addData(a2.totals);
                            })
                        }
                    })
                }
            });
        }

        if (cd.otherTotals) {
            // add "static" totals to the new data set as well:
            allCd.addTotals(cd.otherTotals);
        }
    }

    days.forEach(day => {
        _processDayCampaignData(campaignDataDays[day]);
    });

    // scores individual campaigns, will recalculate totals as well
    scoreCampaignData(allCd);

    return allCd;
}


/**
 * @description Adds all the totals together for each/all groups of campaigns, adsets, ads
 * and then scores each based on the group totals.
 * @param {*} campaignData
 * @return {*}
 */
export function scoreCampaignData(campaignData: DailyCampaignData) {
    if (!campaignData || !campaignData.campaigns) return;

    let campaignTotals = new Totals();
    let adsetTotals = new Totals();
    let adTotals = new Totals();

    // Todo: this needs to be moved to page-specific place... and take in at least 7 days of data:
    //const calculateCampaignMLProbability = (campaign) => {
    // // todo: these should really be moved, or added as sub-set of this data st...
    // campaign.last7Purchases = campaign.days.map(d => d.totals.purchase);
    // campaign.last7Items = campaign.days.map(d => d.totals.items);
    // campaign.last7Orders = campaign.days.map(d => d.totals.orders);
    // campaign.last7Revenue = campaign.days.map(d => d.totals.revenue);
    // campaign.last7Adspend = campaign.days.map(d => d.totals.spend);
    // campaign.last7Roas = campaign.days.map(d => d.totals.roas);
    // campaign.last7Cpp = campaign.days.map(d => d.totals.cpp);
    // campaign.last7Aov = campaign.days.map(d => d.totals.aov);

    // let roas = campaign.totals.roas;
    // let roasRecommendationCode = 0;
    // const [probDecRoas, probIncRoas] = CampaignsML.getProbabilities(campaign);

    // if (roas > roasGoal) {
    //     if ((roas > roasGoal + 1.5) || probIncRoas > 0.50) {
    //         roasRecommendationCode = 1;
    //     }
    // } else if (probDecRoas >= 0.80) {
    //     if (roas < roasGoal) {
    //         roasRecommendationCode = -1;
    //     }
    // } else if (probDecRoas > 0 && roasGoal - roas >= 1.5) {
    //     roasRecommendationCode = -1;
    // }

    // campaign.probIncRoas = probIncRoas;
    // campaign.probDecRoas = probDecRoas;
    // campaign.roasRecommendationCode = roasRecommendationCode;
    //}

    // first ensure it's re-summed:
    campaignData.recalculateTotals();

    // do all summations first:
    campaignData.campaigns.forEach(c => {
        campaignTotals.addData(c.totals);

        if (c.adsets) {
            c.adsets.forEach(adset => {
                adsetTotals.addData(adset.totals);

                if (adset.ads) {
                    adset.ads.forEach(ad => {
                        adTotals.addData(ad.totals);
                    });
                }
            })
        }

        // todo: async await this for each? or do elsewhere.
        //calculateCampaignMLProbability(c);
    });

    // now score all campaigns > adsets > ads, relative to totals:
    //  1. Conversion % vs. Account Average
    //  2. ATC Conv. % vs. Account Average
    //  3. Items/Cust. vs. Account Average

    campaignData.campaigns.forEach(c => {
        calculateScoresFromTotals(c.totals, campaignTotals);

        if (c.adsets) {
            c.adsets.forEach(adset => {
                calculateScoresFromTotals(adset.totals, adsetTotals);

                if (adset.ads) {
                    adset.ads.forEach(ad => {
                        calculateScoresFromTotals(ad.totals, adTotals);
                    });
                }
            })
        }
    });

}

// will return all the metrics for a given, ie. '7-day' range of data:
export const calculateMetricsAcrossDays = (daySet: { [string]: DailyCampaignData }) => {
    log(`calculateMetricsAcrossDays`, daySet);

    const metricsForDays = NEW_DEFAULT_METRICS();

    const days = Object.keys(daySet);

    // add each day of data in the set:
    days.map(day => {
        let dailyCampaignData = daySet[day];
        if (!dailyCampaignData) return;

        let dayTotals = dailyCampaignData.getTotals();

        metricsForDays.orders += orZero(dayTotals.orders);
        metricsForDays.adspend += orZero(dayTotals.adspend);
        metricsForDays.revenue += orZero(dayTotals.revenue);
        metricsForDays.sales += orZero(dayTotals.sales);
        metricsForDays.items += orZero(dayTotals.items);
        metricsForDays.pageviews += orZero(dayTotals.pageviews);
        metricsForDays.sessions += orZero(dayTotals.sessions);
        metricsForDays.addToCarts += orZero(dayTotals.addToCarts);
        metricsForDays.checkouts += orZero(dayTotals.checkouts);

        metricsForDays.ordersData.push(orZero(dayTotals.orders));
        metricsForDays.adspendData.push(orZero(dayTotals.adspend));
        metricsForDays.revenueData.push(orZero(dayTotals.revenue));
        metricsForDays.salesData.push(orZero(dayTotals.sales));
        metricsForDays.itemsData.push(orZero(dayTotals.items));
        metricsForDays.pageviewsData.push(orZero(dayTotals.pageviews));
        metricsForDays.sessionsData.push(orZero(dayTotals.sessions));
        metricsForDays.addToCartsData.push(orZero(dayTotals.addToCarts));
        metricsForDays.checkoutsData.push(orZero(dayTotals.checkouts));

        metricsForDays.cppData.push(orZero(dayTotals.adspend / dayTotals.orders));
        metricsForDays.aovData.push(orZero(dayTotals.revenue / dayTotals.orders));
        metricsForDays.roasData.push(orZero(dayTotals.revenue / dayTotals.adspend));
        metricsForDays.merData.push(orZero(dayTotals.sales / dayTotals.adspend));
        metricsForDays.merPredictionData.push(0);

        metricsForDays.daysData.push(day);
    });

    // calculate averages across the set:
    metricsForDays.ordersAvg = orZero(metricsForDays.orders / days.length);
    metricsForDays.adspendAvg = orZero(metricsForDays.adspend / days.length);
    metricsForDays.revenueAvg = orZero(metricsForDays.revenue / days.length);
    metricsForDays.salesAvg = orZero(metricsForDays.sales / days.length);
    metricsForDays.itemsAvg = orZero(metricsForDays.items / days.length);
    metricsForDays.pageviewsAvg = orZero(metricsForDays.pageviews / days.length);
    metricsForDays.sessionsAvg = orZero(metricsForDays.sessions / days.length);
    metricsForDays.addToCartsAvg = orZero(metricsForDays.addToCarts / days.length);
    metricsForDays.checkoutsAvg = orZero(metricsForDays.checkouts / days.length);

    metricsForDays.cpp = metricsForDays.cppAvg = orZero(metricsForDays.adspend / metricsForDays.orders);
    metricsForDays.aov = metricsForDays.aovAvg = orZero(metricsForDays.revenue / metricsForDays.orders);
    metricsForDays.roas = metricsForDays.roasAvg = orZero(metricsForDays.revenue / metricsForDays.adspend);
    metricsForDays.mer = metricsForDays.merAvg = orZero(metricsForDays.sales / metricsForDays.adspend);
    metricsForDays.merPrediction = metricsForDays.merPredictionAvg = 0;

    // sanity check
    if (orZero(metricsForDays.revenue / metricsForDays.adspend) != orZero(metricsForDays.revenueAvg / metricsForDays.adspendAvg)) {
        console.warn("Current data set roasAvg does not equal roas. Why?",
            orZero(metricsForDays.revenue / metricsForDays.adspend), orZero(metricsForDays.revenueAvg / metricsForDays.adspendAvg));
    }

    log(`calculateMetricsAcrossDays result: `, daySet, metricsForDays);
    return metricsForDays;
}

/**
 * @description Scores the given AdObject relative to it's scores against the totals.
 * @param {Totals} aoTotals
 * @param {Totals} allTotals
 */
export function calculateScoresFromTotals(aoTotals: Totals, allTotals: Totals) {
    if (!aoTotals.scores) aoTotals.scores = new ScoreTotals();

    // we do this again here (it's done by default in Totals.addData too), just in case the numbers got reset.
    aoTotals.scores.itemsPerCustomer = orZero(aoTotals.items / aoTotals.orders);
    aoTotals.scores.conversionRate = orZero((aoTotals.orders / aoTotals.sessions) * 100);
    aoTotals.scores.addToCartConversionRate = orZero(aoTotals.items / aoTotals.addToCarts) * 100;

    allTotals.scores.itemsPerCustomer = orZero(allTotals.items / allTotals.orders);
    allTotals.scores.conversionRate = orZero((allTotals.orders / allTotals.sessions) * 100);
    allTotals.scores.addToCartConversionRate = orZero(allTotals.items / allTotals.addToCarts) * 100;

    // set the relative scores on the ad object:
    aoTotals.scores.conversionIndex = orZero(aoTotals.scores.conversionRate / allTotals.scores.conversionRate) * 100;
    aoTotals.scores.addToCartConversionIndex = orZero(aoTotals.scores.addToCartConversionRate / allTotals.scores.addToCartConversionRate) * 100;
    aoTotals.scores.itemsPerCustomerIndex = orZero(aoTotals.scores.itemsPerCustomer / allTotals.scores.itemsPerCustomer) * 100;

    aoTotals.scores.score = orZero((aoTotals.scores.conversionIndex + aoTotals.scores.addToCartConversionIndex + aoTotals.scores.itemsPerCustomerIndex) / 3);
}

/**
 * @description Goes through the entire data set (campaigns > adsets > ads) and
 * returns a new data set with only the objects that pass the checkFn.
 * This can be used to filter ad object by a specific metric such as adspend > 0 (ie. active only)
 * @param {DailyCampaignData} data
 * @param {*} checkFn Campaigns will be passed to this check function, and be put into the result if they pass.
 * @param {} score If true, it will re-score the resulting data.
 */
export const filterAdObjects = (data: DailyCampaignData, filterFn: (ao: AdObject, aoType?: AdObjectType) => boolean, score: boolean = false) => {
    try {
        if (!data || !data.campaigns || !filterFn) return data;

        // new data set with results:
        const result = new DailyCampaignData(data.dayStart, data.dayEnd); // no day given, assume multi-range.

        data.campaigns.forEach(c => {
            if (filterFn(c, AdObjectType.Campaign)) result.addCampaign(c);
            // if (c.adsets) {
            //     c.adsets.forEach(adset => {
            //         adsetTotals.addData(adset.totals);

            //         if (adset.ads) {
            //             adset.ads.forEach(ad => {
            //                 adTotals.addData(ad.totals);
            //             });
            //         }
            //     })
            // }

            // todo: async await this for each? or do elsewhere.
            //calculateCampaignMLProbability(c);
        });

        // re-score the result set if needed
        if (score) scoreCampaignData(result);

        return result;
    } catch (e) {
        throw e;
    }
}


export const NEW_DEFAULT_METRICS = () => ({
    // sums:
    orders: 0,
    adspend: 0,
    revenue: 0,
    sales: 0, // we use this to distinguish shopify sales vs. revenue.
    items: 0,
    pageviews: 0,
    sessions: 0,
    addToCarts: 0,
    checkouts: 0,
    cpp: 0,
    aov: 0,
    roas: 0,
    mer: 0, // same as roas if shopify data, just a convenience for some tabs
    merPrediction: 0,

    // averages:
    ordersAvg: 0,
    adspendAvg: 0,
    revenueAvg: 0,
    salesAvg: 0,
    itemsAvg: 0,
    pageviewsAvg: 0,
    sessionsAvg: 0,
    addToCartsAvg: 0,
    checkoutsAvg: 0,

    // Note: ROAS is considered/implied the same as MER, depending on which type of data set is pass to this method.
    // MER = from Shopify data, and ROAS = from Adaya/tracking data.
    cppAvg: 0,
    aovAvg: 0,
    roasAvg: 0, // roas = revenue/adspend
    merAvg: 0, // mer = shopify sales/adspend
    merPredictionAvg: 0,

    // data:
    // (ie. individual metric arrays across the days, ie, orderData: [2,4,1,...]
    ordersData: [],
    adspendData: [],
    revenueData: [],
    salesData: [],
    itemsData: [],
    pageviewsData: [],
    sessionsData: [],
    addToCartsData: [],
    checkoutsData: [],
    cppData: [],
    aovData: [],
    roasData: [],
    merData: [],
    merPredictionData: [],

    // string-array of which days this set includess
    daysData: [],
});